import { useCallback } from 'react'
import { getDocumentCustomerSchema } from 'src/app/components/document-customer-input/document-customer-schema'
import * as z from 'zod'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useCustomError } from 'src/utils/custom-errors.hook'
import { SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

export default function useUnpaidDocumentsReminderForm(onSubmit: (data: UnpaidDocumentsReminderFormSchema) => Promise<void>, defaultValue?: Partial<UnpaidDocumentsReminderFormSchema>) {
  const { t } = useTranslation()

  const unpaidDocumentReminderSchema = getUnpaidDocumentReminderForm(t)
  const customErrorMap = useCustomError()
  z.setErrorMap(customErrorMap)

  const {
    control,
    handleSubmit: rhfHandleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useForm<UnpaidDocumentsReminderFormSchema>({ resolver: zodResolver(unpaidDocumentReminderSchema), defaultValues: { ...(defaultValue ?? {}) } })

  const submitHandler: () => SubmitHandler<UnpaidDocumentsReminderFormSchema> = useCallback(() => {
    return async (data: UnpaidDocumentsReminderFormSchema) => {
      await onSubmit(data)
    }
  }, [onSubmit])

  const handleSubmit = useCallback(() => rhfHandleSubmit(submitHandler()), [rhfHandleSubmit, submitHandler])

  return { handleSubmit, control, errors, watch, setValue, getValues }
}

export function getUnpaidDocumentReminderForm(t?: TFunction) {
  return z.object({
    customer: getDocumentCustomerSchema(),
    unpaidDocumentIds: z
      .string()
      .array()
      .refine((v) => {
        return v && v.length >= 1
      }, t?.('pms.commons.document.unpaid_documents_selection_warning')),
  })
}

const unpaidDocumentsReminderFormSchema = getUnpaidDocumentReminderForm()
export type UnpaidDocumentsReminderFormSchema = z.infer<typeof unpaidDocumentsReminderFormSchema>
